import { finiteVec3, idNumber, normalizedIdCounter } from './ids.js';
import { edgeId } from './edges.js';

/** Capture a THREE.BufferGeometry's current position/index buffers as canonical topology. */
export function captureTopology(geometry) {
  const position = geometry?.attributes?.position;
  if (!position) throw new Error('geometry has no position attribute to capture');

  const vertices = [];
  for (let i = 0; i < position.count; i++) {
    vertices.push({ id: `v${i + 1}`, position: [position.getX(i), position.getY(i), position.getZ(i)] });
  }

  const index = geometry.index;
  const indices = index ? index.array : Array.from({ length: position.count }, (_, i) => i);
  if (indices.length % 3 !== 0) {
    throw new Error(`geometry index length ${indices.length} is not a multiple of 3 (non-triangle faces are not supported yet)`);
  }

  const faces = [];
  for (let i = 0; i < indices.length; i += 3) {
    faces.push({
      id: `f${i / 3 + 1}`,
      vertexIds: [`v${indices[i] + 1}`, `v${indices[i + 1] + 1}`, `v${indices[i + 2] + 1}`],
    });
  }

  return { nextVertexId: vertices.length + 1, nextFaceId: faces.length + 1, vertices, faces };
}

/** Deep, structure-only clone — safe to hand out to callers or store independently. */
export function cloneTopology(topology) {
  return structuredClone(topology);
}

/**
 * Validate and normalize topology arriving from outside this module: replay
 * of a persisted session, or a hand-crafted `addMesh` call. Throws a clear
 * error rather than building a geometry from malformed data.
 */
export function parseTopology(raw) {
  if (!raw || typeof raw !== 'object') throw new Error('topology must be an object');
  if (!Array.isArray(raw.vertices) || raw.vertices.length === 0) {
    throw new Error('topology.vertices must be a non-empty array');
  }
  if (!Array.isArray(raw.faces) || raw.faces.length === 0) {
    throw new Error('topology.faces must be a non-empty array');
  }

  const seenVertexIds = new Set();
  const vertices = raw.vertices.map((entry, index) => {
    if (!entry || typeof entry.id !== 'string' || entry.id.length === 0) {
      throw new Error(`topology.vertices[${index}] must have a non-empty string "id"`);
    }
    if (seenVertexIds.has(entry.id)) throw new Error(`topology.vertices has a duplicate id '${entry.id}'`);
    seenVertexIds.add(entry.id);
    return { id: entry.id, position: finiteVec3(entry.position, `topology.vertices[${index}].position`) };
  });

  const seenFaceIds = new Set();
  const faces = raw.faces.map((entry, index) => {
    if (!entry || typeof entry.id !== 'string' || entry.id.length === 0) {
      throw new Error(`topology.faces[${index}] must have a non-empty string "id"`);
    }
    if (seenFaceIds.has(entry.id)) throw new Error(`topology.faces has a duplicate id '${entry.id}'`);
    seenFaceIds.add(entry.id);
    if (!Array.isArray(entry.vertexIds) || entry.vertexIds.length !== 3) {
      throw new Error(`topology.faces[${index}] must have exactly 3 vertexIds (got ${entry.vertexIds?.length ?? 0})`);
    }
    if (new Set(entry.vertexIds).size !== entry.vertexIds.length) {
      throw new Error(`topology.faces[${index}] has a repeated vertex id within one face — a triangle needs 3 distinct corners`);
    }
    for (const vertexId of entry.vertexIds) {
      if (!seenVertexIds.has(vertexId)) {
        throw new Error(`topology.faces[${index}] references unknown vertex id '${vertexId}'`);
      }
    }
    return { id: entry.id, vertexIds: [...entry.vertexIds] };
  });

  // reduce(), not Math.max(...spread): a dense mesh can have tens of
  // thousands of vertices, and spreading that many arguments into Math.max
  // risks "Maximum call stack size exceeded" on some engines.
  const maxVertexNumber = vertices.reduce((max, v) => Math.max(max, idNumber(/^v(\d+)$/, v.id, 'topology.vertices')), 0);
  const maxFaceNumber = faces.reduce((max, f) => Math.max(max, idNumber(/^f(\d+)$/, f.id, 'topology.faces')), 0);

  let explicitEdgeIds;
  if (raw.explicitEdgeIds !== undefined) {
    if (!Array.isArray(raw.explicitEdgeIds)) {
      throw new Error('topology.explicitEdgeIds must be an array when present');
    }

    const rawEdgeIds = new Set();
    for (const face of faces) {
      for (let i = 0; i < face.vertexIds.length; i++) {
        rawEdgeIds.add(edgeId(face.vertexIds[i], face.vertexIds[(i + 1) % face.vertexIds.length]));
      }
    }

    explicitEdgeIds = [...new Set(raw.explicitEdgeIds.map((id, index) => {
      if (typeof id !== 'string') {
        throw new Error(`topology.explicitEdgeIds[${index}] must be a string`);
      }
      if (!rawEdgeIds.has(id)) {
        throw new Error(`topology.explicitEdgeIds entry '${id}' does not exist in topology.faces`);
      }
      return id;
    }))].sort();
  }

  const topology = {
    nextVertexId: normalizedIdCounter(raw.nextVertexId, maxVertexNumber, 'topology.nextVertexId'),
    nextFaceId: normalizedIdCounter(raw.nextFaceId, maxFaceNumber, 'topology.nextFaceId'),
    vertices,
    faces,
  };
  if (explicitEdgeIds !== undefined) topology.explicitEdgeIds = explicitEdgeIds;
  return topology;
}

/** Apply id-addressed absolute position changes in place. Throws on any unknown vertex id (all-or-nothing). */
export function applyVertexPositions(topology, changes) {
  const byId = new Map(topology.vertices.map((vertex) => [vertex.id, vertex]));
  for (const [vertexId] of changes) {
    if (!byId.has(vertexId)) throw new Error(`unknown vertex id '${vertexId}'`);
  }
  for (const [vertexId, position] of changes) {
    byId.get(vertexId).position = position;
  }
}

/** Compact digest for cheap listing — never the full vertex/face arrays. */
export function describeTopology(topology) {
  return { vertexCount: topology.vertices.length, faceCount: topology.faces.length };
}
